<template>
  <div class="relative flex flex-col items-center">
    <button type="button" @click="showCheckoutModal" :disabled="noneReserved()" class="btn z-10 bg-nagreen-light lg:sticky btn-sticky text-white hover:bg-nagreen normal-case my-0 px-6 mb-4">{{ buttonTitle }}</button>
    <table class="mb-4 butique-map border-gray-400 border-0" :style="mapBackgroundImage">
      <tbody>
        <tr class="bg-white text-center">
          <td></td>
          <td v-for="letter in letters" :class="letter==='T' ? 'border-white border-r' : 'border-gray-400'" class="border-b text-gray-500 text-xxs sm:text-sm">{{ letter }}</td>
        </tr>
        <tr v-for="row in 21" :key="row" :class="{ 'border-b' : row==21 }">
          <td :class="row==21 ? 'border-white border-b' : 'border-gray-400'" class="border-r bg-white text-gray-500 text-xxs sm:text-sm">{{ row }}</td>
          <td v-for="col in 20" class="overflow-hidden border-0 p-0" :class="{ 'border-r' : col==20}" :key="row-col">
            <a :class="pixelClasses(row, col)"
              class="block vw-20"
              :title="pixelTitle(row, col)"
              :href="pixelUrl(row, col)"
              @click.prevent="showOrReservePixel(row, col, $event)"
              :style="pixelStyle(row, col)"></a>
          </td>
        </tr>
      </tbody>
    </table>
    <modal transition="overlay-fade"
      :clickToClose="false"
      :delay="100"
      :adaptive="true"
      name="checkoutModal"
      height="auto"
      :scrollable="true"
      :maxWidth="384"
      classes="mt-2 mb-8i"
      width="100%">

      <checkout
        @close="closeCheckoutModal"
        @success="showThankYouModal"
        type="forestSquare"
        :price="price"
        title="Skogsrutor"
        :url="url"
        :from="from"
        :to="to"
        :prePaymentStepDone="detailsFilledIn && giftDetailsFilledIn"
        :orderId="orderId"
        :mapId="mapId"
        :paymentTimeout="15"
        :forestSquares="reservedPixels">
        <template v-if="!detailsFilledIn || !giftDetailsFilledIn" #prePaymentStep>
          <transition name="slide-left">
            <div v-if="!detailsFilledIn" class="h-full" key="details">
              <h3 class="pt-4 mb-1 text-left text-lg">Detaljer för skogsrutan</h3>
              <span class="text-smaller text-gray-700">Dessa uppgifter kommer att vara synliga för andra användare och om du vill kan du lämna dem tomma.</span>
              <div class="mt-4">
                <TextInput v-model="title" name="Titel" type="text" placeholder="Titel" :validate="false"></TextInput>
              </div>
              <div class="mt-4">
                <TextInput v-model="url" name="URL" type="text" placeholder="URL" :rules="{ regex: /^(https?:\/\/)?(www)?\w+\.\w{2,}.*$/ }" :customMessages="{regex: 'Lämna tomt eller ange en giltig url'}"></TextInput>
              </div>
              <div class="mt-4">
                <label class="cursor-pointer">
                  <div class="btn btn-border btn-sm py-1 px-2 mt-0">Välj bild</div>
                  <input type="file" id="file" ref="file" class="inputfile" v-on:change="handleFileUpload()"/>
                </label>
                <img v-if="uploadedImage" :src="uploadedImageSrc" class="inline" :style="imageStyle" >
                <span class="text-smaller block mt-1 text-gray-500">Bilden kommer automatiskt anpassas för att passa ovanpå de skogsrutor du köper.</span>
              </div>
              <div class="text-center my-4">
                <span v-if="error" class="text-error block bg-red-100 rounded p-2 mb-1">{{ error }}</span>
                <button type="button" class="btn btn-border mt-0 normal-case" @click="detailsFilledIn = true">
                  Nästa steg
                </button>
              </div>
            </div>
            <div v-if="detailsFilledIn && !giftDetailsFilledIn" class="h-full" key="gift-details">
              <h3 class="pt-4 mb-1 text-left text-lg">Detaljer för gåvobeis</h3>
              <span class="text-smaller text-gray-700">Dessa uppgifter används bara för ditt gåvobevis och är inte synliga för någon annan än dig.</span>
              <div class="mt-4">
                <TextInput v-model="from" name="Från" type="text" placeholder="Från" rules="required"></TextInput>
              </div>
              <div class="mt-4">
                <TextInput v-model="to" name="Till" type="text" placeholder="Till" rules="required"></TextInput>
              </div>
              <div class="text-center my-4">
                <span v-if="error" class="text-error block bg-red-100 rounded p-2">{{ error }}</span>
                <button type="button" class="btn btn-border mt-0 normal-case" @click="proceedToPayment" :disabled="loading">
                  Till betalning
                </button>
              </div>
            </div>
          </transition>
        </template>
      </checkout>
    </modal>
    <ThankYouModal
      :show="showThankYou"
      :print="false"
      :assets="assets"
      :orderId="orderId"
      :pdf="pdf"
      title="Tack för din gåva!"
      button1Title="Stäng"
      :button1OnClick="reload">
    </ThankYouModal>
  </div>
</template>
<script>
  import axios from 'axios'
  import Checkout from './Checkout.vue'
  import ThankYouModal from './ThankYouModal.vue'
  import TextInput from './TextInput.vue'
  import loadImage from 'blueimp-load-image'
  import Bugsnag from '@bugsnag/js'

  export default {
    name: 'ForestMap',
    components: {
      Checkout,
      ThankYouModal,
      TextInput,
    },
    data() {
      return {
        reservedPixels: [],
        loading: false,
        title: undefined,
        url: undefined,
        showThankYou: false,
        email: undefined,
        uploadedImage: undefined,
        uploadedImageSrc: '',
        error: undefined,
        detailsFilledIn: false,
        giftDetailsFilledIn: false,
        orderId: undefined,
        pdf: undefined,
        from: undefined,
        to: undefined,
        fullImage: undefined,
        letters: ["A","B","C","D","E","F","G","H","I","J","K","L","M","N","O","P","Q","R","S","T"],
      }
    },
    computed: {
      mapBackgroundImage() {
        return `background-image: url( ${this.mapImage} )`
      },
      buttonTitle() {
        const num = this.reservedPixels.length
        return num ? `Köp ${num} ${num > 1 ? 'skogsrutor' : 'skogsruta'}` : 'Inga skogsrutor valda'
      },
      colsReserved() {
        const maxCol = this.reservedPixels.reduce((max, p) => p.col > max ? p.col : max, 
            this.reservedPixels[0].col)
        return maxCol - this.minReservedCol + 1
      },
      minReservedCol() {
        return this.reservedPixels.reduce((min, p) => p.col < min ? p.col : min,
          this.reservedPixels[0].col)
      },
      minReservedRow() {
        return this.reservedPixels.reduce((min, p) => p.row < min ? p.row : min, 
          this.reservedPixels[0].row)
      },
      rowsReserved() {
        const maxRow = this.reservedPixels.reduce((max, p) => p.row > max ? p.row : max, 
            this.reservedPixels[0].row)
        return maxRow - this.minReservedRow + 1
      },
      imageWidth() {
        if (this.reservedPixels.length == this.colsReserved * this.rowsReserved) {
          return this.colsReserved * 33
        }
        return 33
      },
      imageHeight() {
        if (this.reservedPixels.length == this.colsReserved * this.rowsReserved) {
          return this.rowsReserved * 33
        }
        return 33
      },
      imageStyle() {
        let width = this.imageWidth,
          height = this.imageHeight

        if (width > 260) {
          height = height*260/width
          width = 260
        }
        if (height > 66) {
          width = width*66/height
          height = 66
        }
        return `width: ${width}px; height: ${height}px`
      }
    },
    methods: {
      imagePartFor(pixel) {
        if (!Array.isArray(this.uploadedImage)) {
          return this.uploadedImage
        }
        return this.uploadedImage.find(obj => {
          return obj.dx == (pixel.col - this.minReservedCol) &&
            obj.dy == (pixel.row - this.minReservedRow)
        })['image']
      },
      async saveImage(image) {
        let formData = new FormData()
        formData.append('file', image, image['name'])
        try {
          return await axios.post( '/wp-json/naturarvet/v1/saveImage', formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          )
        } catch(e) {
          Bugsnag.notify(e)
          throw 'Kunde inte ladda upp den angivna bilden, försök igen eller välj en annan bild'
        }
      },
      async proceedToPayment() {
        if (!this.from && !this.to) {
          this.error = "Fyll i alla uppgifter"
          return
        }
        try {
          this.loading = true
          let uploadedImageIds = []
          if (this.uploadedImage) {
            await this.saveImage(this.fullImage)
            for(const pix of this.reservedPixels) {
              const result = await this.saveImage(this.imagePartFor(pix))
              uploadedImageIds.push({
                id: result.data.attachment_id,
                row: pix.row,
                col: pix.col
              })
            }
          }
          const res = await axios.post( '/wp-json/naturarvet/v1/reservePixels', {
            'mapId': this.mapId,
            'coordinates': this.reservedPixels,
            'imageIds': uploadedImageIds,
            'title': this.title,
            'url': this.url,
            'from': this.from,
            'to': this.to,
          })
          this.orderId = res.data.orderId
          this.giftDetailsFilledIn = true
          this.loading = false
        } catch(e) {
          this.error = "Något gick fel, vänligen försök igen eller kontakta givarservice på info@naturarvet.se om felet kvarstår."
          if (typeof e == 'string') {
            this.error = e
          }
          else if (e.response && e.response.data && e.response.data.message) {
            this.error = e.response.data.message
          }
          Bugsnag.notify(e)
          this.loading = false
        }
      },
      async handleFileUpload(){
        const file = this.$refs.file.files[0]
        if(!file || !file.type.match(/image.*/)) {
          this.error = 'Ogiltig bild vald'
          return
        }
        // Scale image to maxwidth and height to upload a smaller version of the original image,
        // in case the splitting of the image fails
        let data = await loadImage(file, {
          maxWidth: 33*20,
          maxHeight: 33*20,
          orientation: true,
          meta: true,
          canvas: true,
        })
        const resized = this.dataURLToBlob(data.image.toDataURL(file.type))
        // new File doesn't work in internet explorer
        //this.fullImage = new File([resized], file.name, {type: file.type})
        this.fullImage = this.newFile(resized, file.name)
        //Now crop image to match bought squares aspect ratio
        const resizedImageWidth = this.imageWidth * 10,
          resizedImageHeight = this.imageHeight * 10
        data = await loadImage(file, {
          maxWidth: resizedImageWidth,
          maxHeight: resizedImageHeight,
          orientation: true,
          meta: true,
          canvas: true,
          cover: true,
          crop: true,
        })
        const dataUrl = data.image.toDataURL(file.type),
          cropped = this.dataURLToBlob(dataUrl)
        this.uploadedImageSrc = dataUrl
        const croppedImage = this.newFile(cropped, file.name)

        if (this.imageWidth == 33 && this.imageHeight == 33) {
          // 1 square or not connected squares bought so no need to split image
          this.uploadedImage = croppedImage
          return
        }
        this.uploadedImage = []
        // Split image across squares
        for (var i = 0; i < this.colsReserved; i++) {
          for (var j = 0; j < this.rowsReserved; j++) {
            data = await loadImage(croppedImage, {
              crop: true,
              canvas: true,
              contain: true,
              pixelRatio: window.devicePixelRatio,
              orientation: true,
              meta:true,
              maxWidth: 330,
              maxHeight: 330,
              left: i*330,
              top: j*330,
              sourceWidth: 330,
              sourceHeight: 330
            })
            const resizedImagePart = this.dataURLToBlob(data.image.toDataURL(file.type))
            this.uploadedImage.push({
              dx: i,
              dy: j,
              image: this.newFile(resizedImagePart, file.name)
            });
          }
        }
      },
      newFile(blob, name) {
        blob['lastModifiedDate'] = new Date()
        blob['name'] = name
        return blob
      },
      dataURLToBlob(dataURL) {
        var BASE64_MARKER = ';base64,'
        if (dataURL.indexOf(BASE64_MARKER) == -1) {
          const parts = dataURL.split(','),
            contentType = parts[0].split(':')[1],
            raw = parts[1]
          return new Blob([raw], {type: contentType})
        }

        const parts = dataURL.split(BASE64_MARKER),
          contentType = parts[0].split(':')[1],
          raw = window.atob(parts[1]),
          rawLength = raw.length

        var uInt8Array = new Uint8Array(rawLength)

        for (var i = 0; i < rawLength; ++i) {
          uInt8Array[i] = raw.charCodeAt(i)
        }
        return new Blob([uInt8Array], {type: contentType});
      },
      reload() {
        location.reload()
      },
      showThankYouModal(e) {
        this.$modal.hide('checkoutModal')
        this.showThankYou = true
        this.email = e.email
        this.pdf = e.pdf
      },
      async closeCheckoutModal() {
        document.body.classList.remove('modal-block-scroll')
        this.$modal.hide('checkoutModal')
        this.uploadedImageSrc = ''
        this.uploadedImage = undefined
        this.error = undefined
        try {
          await axios.post( '/wp-json/naturarvet/v1/unreservePixels', {
            'orderId': this.orderId,
            'mapId': this.mapId,
          })
        } catch(e) {
          Bugsnag.notify(e)
          // Do nothing, backend cleanup job will clean up the reserved pixels
        }
      },
      showCheckoutModal() {
        this.detailsFilledIn = false
        this.giftDetailsFilledIn = false
        try {
          document.body.classList.add('modal-block-scroll')
          this.$modal.show('checkoutModal');        
        } catch(e) {
          Bugsnag.notify(e)
        }
      },
      noneReserved() {
        return this.reservedPixels.length == 0
      },
      pixelClasses(row, col) {
        return {
          'map-bought-pixel': this.findPixel(row,col),
          'bg-gray-200 opacity-50': this.pixelReserved(row,col)
        }
      },
      pixelReserved(row, col) {
        return this.reservedPixels.find(elm => elm.row == row && elm.col == col)
      },
      pixelBought(row, col) {
        const pixel = this.findPixel(row, col)
        return pixel && pixel.status != 'pending'
      },
      pixelUrl(row, col) {
        return this.fullPixelUrl(this.findPixel(row, col))
      },
      pixelTitle(row, col) {
        const pixel = this.findPixel(row, col)
        if (pixel) {
          if (pixel.status == 'pending') {
            return 'Den här skogsytan är reserverad';
          }
          return pixel.pixel_title;
        }
        return 'Köp en skogsyta';
      },
      pixelStyle(row, col) {
        const pixel = this.findPixel(row, col)
        if (!pixel) {
          return ''
        }
        if (pixel.status == 'pending') {
          return 'background: rgba( 255, 255, 255, .4 ); cursor: not-allowed;'
        }
        if (this.hasValidImageURL(pixel)) {
          return this.validURL(pixel.pixel_url) ?
            `background-image: url( ${pixel.image[0]} )` :
            `background-image: url( ${pixel.image[0]} ); cursor: default;`
        }
        return this.validURL(pixel.pixel_url) ? '' : 'cursor: default'
      },
      hasValidImageURL(pixel) {
        return pixel && pixel.image ? this.validURL(pixel.image[0]) : false
      },
      validURL(url) {
        return url && url.toString && url.toString().match(/(http(s)?:\/\/)?[-a-zA-Z0-9@:%./_+~#=]{2,256}\.[a-z]{2,6}/)
      },
      fullPixelUrl(pixel) {
        if (!pixel || !this.validURL(pixel.pixel_url)) return ''

        return !pixel.pixel_url.toString().match(/^https?:\/\//i) ?
          'http://' + pixel.pixel_url : pixel.pixel_url
      },
      showOrReservePixel(row, col, event) {
        event.preventDefault()
        const pixel = this.findPixel(row, col)
        if (pixel) {
          this.validURL(pixel.pixel_url) ? window.open(this.fullPixelUrl(pixel), '_blank') : null
        } else {
          if (this.pixelReserved(row, col)) {
            this.reservedPixels = this.reservedPixels.filter(elm => !(elm.row == row && elm.col == col))
          } else {
            this.reservedPixels.push({ row, col })
          }
        }
      },
      findPixel(row, col) {
        return this.boughtPixels.find(elm => elm.pos_x == col && elm.pos_y == row)
      }
    },
    props: {
      'mapId': Number,
      'mapImage': String,
      'boughtPixels': Array,
      'assets': String,
      'price': Number,
    }
  }
</script>
<style lang="scss" scoped>
  .overlay-fade-enter-active,
  .overlay-fade-leave-active {
    transition: all 0.2s;
  }

  .overlay-fade-enter,
  .overlay-fade-leave-active {
    opacity: 0;
  }
  @screen lg {
    .btn-sticky {
      top: 85px;
    }
  }
  .inputfile {
    width: 0.1px;
    height: 0.1px;
    opacity: 0;
    overflow: hidden;
    position: absolute;
    z-index: -1;
  }
  /* calculate width of each column to let the map fill all available space on small devices
   * 20 = number of columns 
   */
  .vw-20 {
    width: calc(100vw/21 - 1px);
    height: calc(100vw/21 - 1px);
  }
  @screen md {
    .vw-20 {
      @apply w-8 h-8
    }
  }
  .bg-origin-content {
    background-origin: content-box;
  }
</style>
